import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useUpdateBillingEntity } from 'src/features/billingEntity/api/useUpdateBillingEntity';
import { LIST_BILLING_ENTITIES_RESOURCE } from 'src/features/billingEntity/api/useListBillingEntities';
import { billingMessages } from 'src/features/billingEntity/utils/messages';
import BillingEntityFormModal from 'src/features/billingEntity/components/forms/BillingEntityFormModal';
import { BillingEntity } from 'src/features/billingEntity/utils/types';
import { useParking } from 'src/providers/ParkingProvider';

interface UpdateBillingEntityFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillingEntity: BillingEntity;
    children?: React.ReactNode;
}

function UpdateBillingEntityFormModal<TData>({
    className,
    semantics,
    children,
    selectedBillingEntity,
}: UpdateBillingEntityFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateBillingEntity({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_ENTITIES_RESOURCE] });
            toast({
                title: formatMessage(billingMessages.updateBillingEntitySuccess),
                description: formatMessage(billingMessages.updateBillingEntitySuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <BillingEntityFormModal
            selectedBillingEntity={selectedBillingEntity}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, parkingId, billingEntityId: selectedBillingEntity.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(billingMessages.editBillingEntitySubtitle, {
                entity: selectedBillingEntity.name,
            })}
        >
            {children}
        </BillingEntityFormModal>
    );
}

export default UpdateBillingEntityFormModal;
